import { useCallback, useEffect, useRef } from 'react';

/**
 * Returns a stable callback reference that always calls the latest version of the provided function.
 * This is useful when you need to pass a callback to a dependency array but the callback
 * implementation changes frequently.
 *
 * Unlike useCallback, this hook doesn't require a dependency array because it always
 * calls the latest function while maintaining a stable reference.
 *
 * @example
 * ```tsx
 * const stableHandler = useStableCallback((value: string) => {
 *   // This can access latest props/state without being in deps
 *   console.log(currentState, value);
 * });
 *
 * useEffect(() => {
 *   // stableHandler reference never changes
 *   api.subscribe(stableHandler);
 *   return () => api.unsubscribe(stableHandler);
 * }, [stableHandler]); // Safe - reference is stable
 * ```
 */
export function useStableCallback<TArguments extends unknown[], TResult>(callback: (...arguments_: TArguments) => TResult): (...arguments_: TArguments) => TResult {
  const callbackReference = useRef(callback);
  callbackReference.current = callback;

  // The outer callback never changes reference
  return useCallback((...arguments_: TArguments) => callbackReference.current(...arguments_), []);
}

/**
 * Similar to useStableCallback but specifically for event handlers.
 * Provides better type inference for DOM events.
 *
 * @example
 * ```tsx
 * const handleClick = useEventCallback((e: MouseEvent) => {
 *   console.log(count); // Always sees latest count
 * });
 *
 * return <button onClick={handleClick}>Click</button>;
 * ```
 */
export function useEventCallback(handler) {
  return useStableCallback(handler);
}

/**
 * Returns a stable async callback that properly handles errors and cleanup.
 * Prevents calling the callback after component unmount.
 *
 * @example
 * ```tsx
 * const fetchData = useAsyncCallback(async (id: string) => {
 *   const data = await api.fetch(id);
 *   setData(data); // Safe even if component unmounts during fetch
 * });
 * ```
 */
export function useAsyncCallback<TArguments extends unknown[], TResult>(callback: (...arguments_: TArguments) => Promise<TResult>): (...arguments_: TArguments) => Promise<TResult | undefined> {
  const isMountedReference = useRef(true);
  const callbackReference = useRef(callback);
  callbackReference.current = callback;
  useEffect(() => () => {
    isMountedReference.current = false;
  }, []);
  return useCallback((...arguments_: TArguments) => {
    if (!isMountedReference.current) {
      return Promise.resolve<TResult | undefined>(globalThis.undefined);
    }
    return callbackReference.current(...arguments_);
  }, []);
}